import { FocusEvent, useCallback, useMemo, useState } from 'react'

type UseAutoCompleteProps<T> = {
  data: T[]
  getDisplayValue: (item: T) => string
  getSearchValue: (item: T) => string
  onSearchValueChange: (value: string) => void
  onSelectedValueChange: (value?: T) => void
  searchValue: string
  selectedValue: null | T
}

export function useAutoComplete<T>({
  data,
  getDisplayValue,
  getSearchValue,
  onSearchValueChange,
  onSelectedValueChange,
  searchValue,
  selectedValue,
}: UseAutoCompleteProps<T>) {
  const [open, setOpen] = useState(false)

  // Create a mapping from searchValue to item
  const searchValueToItemMap = useMemo(() => {
    const map = new Map<string, T>()
    data.forEach(item => {
      const key = getSearchValue(item)
      map.set(key, item)
    })
    return map
  }, [data, getSearchValue])

  // Determine the current display value based on the selected item
  const displayValue = useMemo(() => {
    if (selectedValue) {
      return getDisplayValue(selectedValue)
    }
    return searchValue
  }, [selectedValue, getDisplayValue, searchValue])

  const reset = useCallback(() => {
    onSelectedValueChange(undefined)
    onSearchValueChange('')
  }, [onSelectedValueChange, onSearchValueChange])

  const onInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      // If the blur is not caused by interacting with the command list, reset if necessary
      if (
        !e.relatedTarget?.hasAttribute('cmdk-list') &&
        (selectedValue === null || getDisplayValue(selectedValue) !== searchValue)
      ) {
        reset()
      }
      setOpen(false)
    },
    [selectedValue, getDisplayValue, searchValue, reset],
  )

  const onSelectItem = useCallback(
    (searchKey: string) => {
      const selected = searchValueToItemMap.get(searchKey)
      if (selected) {
        if (selectedValue && getSearchValue(selectedValue) === searchKey) {
          reset()
        } else {
          onSelectedValueChange(selected)
          onSearchValueChange(getDisplayValue(selected))
        }
      }
      setOpen(false)
    },
    [
      searchValueToItemMap,
      selectedValue,
      getSearchValue,
      getDisplayValue,
      onSelectedValueChange,
      onSearchValueChange,
      reset,
    ],
  )

  return {
    displayValue,
    onInputBlur,
    onSelectItem,
    open,
    setOpen,
  }
}
